/**
 * SynergyPanel — Cross-Project Synergy Analysis display (Story #3867).
 *
 * Shown when exactly two projects are selected in CompareProjectsPanel.
 * Calls GET /crosspollination/synergy-pair and renders:
 *   - overall score badge (0–100)
 *   - top 5 shared themes with confidence bars
 *   - top 3 complementary areas (cross-type component pairs)
 *   - top 2 differentiating areas per project
 *
 * States: loading → available (renders analysis) | not_available (no embeddings)
 * Error state falls through to not_available message.
 */
import { useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Sparkles, RefreshCw, Loader2, AlertCircle } from 'lucide-react'
import { getSynergyPair } from '../../services/api'
import type { Project, SynergyAnalysis, SynergySharedTheme, SynergyArea } from '../../types'
import { shortDate } from '../../lib/shortDate'

interface Props {
  projectA: Project
  projectB: Project
  onClose: () => void
}

function ScoreBadge({ score }: { score: number }) {
  const colour =
    score >= 70
      ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
      : score >= 40
      ? 'bg-amber-100 text-amber-700 border-amber-200'
      : 'bg-red-100 text-red-600 border-red-200'
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold border ${colour}`}
      data-testid="synergy-overall-score"
    >
      {score}% synergy
    </span>
  )
}

function ConfidenceBar({ value }: { value: number }) {
  const pct = Math.round(value * 100)
  const colour = pct >= 70 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-400' : 'bg-red-400'
  return (
    <div className="flex items-center gap-2 mt-0.5">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${colour}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-mono text-gray-500 w-8 text-right">{pct}%</span>
    </div>
  )
}

function SharedThemeList({ themes }: { themes: SynergySharedTheme[] }) {
  if (!themes.length) return <p className="text-xs text-gray-500 italic">None identified</p>
  return (
    <ul className="space-y-2" data-testid="synergy-shared-themes">
      {themes.map((t) => (
        <li key={t.source_component_id + t.target_component_id} className="text-xs">
          <div className="flex gap-1">
            <span className="font-medium text-gray-700 truncate flex-1" title={t.source_title}>
              {t.source_title}
            </span>
            <span className="text-gray-500">↔</span>
            <span className="font-medium text-gray-700 truncate flex-1" title={t.target_title}>
              {t.target_title}
            </span>
          </div>
          <ConfidenceBar value={t.confidence} />
        </li>
      ))}
    </ul>
  )
}

function ComplementaryAreaList({ areas }: { areas: SynergyArea[] }) {
  if (!areas.length) return <p className="text-xs text-gray-500 italic">None identified</p>
  return (
    <ul className="space-y-2" data-testid="synergy-complementary-areas">
      {areas.map((a, i) => (
        <li key={i} className="text-xs">
          <div className="flex gap-1 items-center">
            <span className="px-1 py-0.5 bg-blue-50 text-blue-600 rounded text-xs uppercase tracking-wide">
              {a.source_type}
            </span>
            <span className="font-medium text-gray-700 truncate">{a.source_title}</span>
            <span className="text-gray-500">+</span>
            <span className="px-1 py-0.5 bg-purple-50 text-purple-600 rounded text-xs uppercase tracking-wide">
              {a.target_type}
            </span>
            <span className="font-medium text-gray-700 truncate">{a.target_title}</span>
          </div>
          {a.confidence !== undefined && <ConfidenceBar value={a.confidence} />}
        </li>
      ))}
    </ul>
  )
}

function DifferentiatingAreaList({
  areasA,
  areasB,
  nameA,
  nameB,
}: {
  areasA: SynergyArea[]
  areasB: SynergyArea[]
  nameA: string
  nameB: string
}) {
  const Side = ({ areas, name }: { areas: SynergyArea[]; name: string }) => (
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-500 font-medium truncate mb-1" title={name}>
        {name}
      </p>
      {!areas.length ? (
        <p className="text-xs text-gray-500 italic">None</p>
      ) : (
        <ul className="space-y-1">
          {areas.map((a, i) => (
            <li key={i} className="text-xs text-gray-700 truncate" title={a.title}>
              {a.title}
              {a.uniqueness_score !== undefined && (
                <span className="ml-1 text-gray-500">({Math.round(a.uniqueness_score * 100)}%)</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
  return (
    <div className="flex gap-4" data-testid="synergy-differentiating-areas">
      <Side areas={areasA} name={nameA} />
      <Side areas={areasB} name={nameB} />
    </div>
  )
}

function SynergyContent({
  analysis,
  projectA,
  projectB,
  onRefresh,
  isRefreshing,
}: {
  analysis: SynergyAnalysis
  projectA: Project
  projectB: Project
  onRefresh: () => void
  isRefreshing: boolean
}) {
  return (
    <div className="space-y-4" data-testid="synergy-content">
      <div className="flex items-center gap-3">
        <ScoreBadge score={analysis.overall_score} />
        <span className="text-xs text-gray-500">
          Cached {shortDate(analysis.cached_at)}
        </span>
        <button
          type="button"
          onClick={onRefresh}
          disabled={isRefreshing}
          className="ml-auto flex items-center gap-1 text-xs text-gray-500 hover:text-gray-600 transition-colors disabled:opacity-50"
          aria-label="Refresh synergy analysis"
          data-testid="synergy-refresh-btn"
        >
          <RefreshCw size={12} className={isRefreshing ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div>
        <h5 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
          Shared Themes
        </h5>
        <SharedThemeList themes={analysis.shared_themes} />
      </div>

      <div>
        <h5 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
          Complementary Areas
        </h5>
        <ComplementaryAreaList areas={analysis.complementary_areas} />
      </div>

      <div>
        <h5 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
          Differentiating Areas
        </h5>
        <DifferentiatingAreaList
          areasA={analysis.differentiating_areas.project_a}
          areasB={analysis.differentiating_areas.project_b}
          nameA={projectA.name}
          nameB={projectB.name}
        />
      </div>
    </div>
  )
}

export default function SynergyPanel({ projectA, projectB, onClose }: Props) {
  // BUG-142: force_refresh is carried on a ref, NOT in the queryKey. The old
  // code appended `forceRefresh` to the queryKey, so a refresh fetched fresh
  // data under a NEW key [...true] and then — once the key flipped back to
  // [...false] — React Query served the still-stale [...false] cache entry,
  // silently discarding the just-fetched result. Keeping a stable base key and
  // refetching writes the fresh result straight back into the cache the UI reads.
  const forceRefreshRef = useRef(false)
  const queryKey = ['synergy-pair', projectA.project_id, projectB.project_id]

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey,
    queryFn: () => {
      const force = forceRefreshRef.current
      forceRefreshRef.current = false
      return getSynergyPair(projectA.project_id, projectB.project_id, force)
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

  const handleRefresh = () => {
    forceRefreshRef.current = true
    refetch()
  }

  return (
    <div
      className="border-t border-gray-200 pt-4 mt-2"
      data-testid="synergy-panel"
      role="region"
      aria-label="Synergy analysis"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={14} className="text-iflow-500" />
        <h4 className="text-sm font-semibold text-gray-900">Cross-Project Synergy</h4>
        <button
          type="button"
          onClick={onClose}
          className="ml-auto text-xs text-gray-500 hover:text-gray-600 transition-colors"
          aria-label="Close synergy panel"
          data-testid="synergy-close-btn"
        >
          Hide
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-xs text-gray-500 py-3" data-testid="synergy-loading">
          <Loader2 size={14} className="animate-spin" />
          Analysing component overlap…
        </div>
      )}

      {(isError || data?.status === 'not_available') && !isLoading && (
        <div className="flex items-start gap-2 text-xs text-gray-500 py-3" data-testid="synergy-not-available">
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            Synergy analysis is not available for this pair yet. Both projects need at least one
            decomposed idea component before analysis can run.
          </span>
        </div>
      )}

      {!isLoading && !isError && data?.status === 'available' && data.analysis && (
        <SynergyContent
          analysis={data.analysis}
          projectA={projectA}
          projectB={projectB}
          onRefresh={handleRefresh}
          isRefreshing={isFetching}
        />
      )}
    </div>
  )
}
